import { useEffect, useState } from 'preact/hooks';
import { useRevalidate } from '../state/useRevalidate';
import { ageInWords, recall, remember } from '../state/cache';
import { activeDestination } from '../state/destinations';
import type { Invalidation } from '../state/events';
import { request, ApiError, type Session, describeError } from '../api/client';
import type {
  WireCalendarEvent,
  WireEntry,
  WireDay,
  WireEventRange,
  WireReflection,
  WireTracker,
} from '../api/wire';
import { nextDay } from '../api/mutations';
import {
  dayOfYear,
  displayDate,
  entryRow,
  eventRow,
  calendarWindowEnd,
  previousDay,
  trackerChip,
  weekdayLabel,
} from '../api/adapter';

// Components are the design system's, copied verbatim. Their .d.ts files carry
// the props contract; only their internals are unchecked.
import { EntryLine } from '../components/composed/EntryLine';
import { SwipeRow } from '../components/composed/SwipeRow';
import { SectionHeading } from '../components/composed/SectionHeading';
import { EventRow } from '../components/calendar/EventRow.jsx';
import { Pressable } from '../components/a11y/Pressable';
import { TrackerToggle } from '../components/composed/TrackerToggle';
import { EmptyState } from '../components/feedback/EmptyState.jsx';
import { PhotoStrip, PhotoViewer } from '../components/composed/Photos';
import { deletePhoto, type Photo } from '../api/photos';
import { logUnit, undoUnit } from '../api/habits';
import { NOTE_SEPARATOR } from '../lib/text';

interface Loaded {
  day: WireDay;
  events: WireEventRange | null;
  tracker: WireTracker | null;
  reflection: WireReflection | null;
  photos: Photo[];
}

/** The schedule band caps at four events so a busy calendar can never push the
 *  rapid log off screen. */
const SCHEDULE_CAP = 4;

export function Today({
  session,
  date,
  onOpenActions,
  onEditReflection,
  onChangeDate,
  invalidation,
  showDayOfYear = true,
  onOpenEvent,
  lanes = 1,
  onMeasureTitles,
  refresh,
  chrome = 'page',
  marksToday = false,
  includeRitual = true,
  paired = false,
  basis,
  showPaging = true,
  active = true,
  badge = 'FOCUSED',
  onQuickAction,
}: {
  session: Session;
  /** Which day to show. Omitted means "whatever the server calls today", which
   *  depends on its timezone and is not the device's idea of today. */
  date?: string;
  /** Tapping an already-selected entry opens its action sheet. Selection first,
   *  then action, so a mis-tap on a dense list cannot mutate anything. */
  onOpenActions: (entry: WireDay['entries'][number]) => void;
  /** The closing ritual is writable — the one place the product is prose. */
  onEditReflection: () => void;
  /** Day paging. Null returns to the server's today. */
  onChangeDate?: (next: string | null) => void;
  /** The latest change recorded by the service. Reloads this day only when the
   *  change touched it. */
  invalidation?: Invalidation | null;
  /** `DAY 214` beside the date. A Settings preference, off for anyone who reads
   *  it as noise. */
  showDayOfYear?: boolean;
  /** Opening a calendar event. Read-only everywhere, per R9 — this hands the
   *  event up for display and nothing else. */
  onOpenEvent?: (event: WireCalendarEvent, range: { asOf: string | null; freshness: string | null }) => void;
  /** Lanes inside this day: schedule beside the rapid log, or stacked. */
  lanes?: 1 | 2;
  /** R12: the widest calendar title this day holds, reported so the layout can
   *  drop a column when it would not fit. Keyed by the resolved date, because
   *  the caller only knows `null` for "today". */
  onMeasureTitles?: (journalDate: string, longestTitle: string) => void;
  /** Bumped when something on this device changed the journal. A *signal*, not
   *  a key: keying a screen on it remounted the whole surface, which threw away
   *  scroll position and reading position on every completed task. The load
   *  effects here keep what is on screen and swap it when the fetch lands, so a
   *  refetch is invisible. */
  refresh?: number;

  /** Who draws the breadcrumb, the layout badge and the day pager. On a spread
   *  that chrome belongs to the row above both pages — printed per page it was
   *  duplicated, or drawn on one page only and pushed that page's content out of
   *  line with its neighbour. */
  chrome?: 'page' | 'spread';
  /** Whether this page is the journal's today, which the spread frame marks
   *  beside the date instead of inside a pager. */
  marksToday?: boolean;
  /** False when the closing ritual has been lifted into a band across a spread,
   *  so it must not also appear in the column. */
  includeRitual?: boolean;
  /** One of two pages side by side. Such a page fills its column and scrolls
   *  on its own even when it carries its own ritual, which is what used to
   *  decide the framing. */
  paired?: boolean;
  /** A fixed width for this page of a spread, so the gap between pages can sit
   *  on a foldable's seam rather than at the middle of whatever the safe area
   *  left over. The other page takes the rest. */
  basis?: string;
  /** Day paging belongs to the active page only — two sets of arrows on a
   *  spread would be two ways to move the same thing. */
  showPaging?: boolean;
  /** The page carrying the amber edge on a spread. */
  active?: boolean;
  /** What the header states about the layout, without the leading `◆`. Null on
   *  a page that should say nothing. */
  badge?: string | null;
  /** A swipe's transition. Runs through App so it shares the toast and its undo
   *  with the action sheet — the gesture is a shortcut to the same mutation, not
   *  a second path to it. */
  onQuickAction?: (entry: WireEntry, action: 'complete' | 'migrate') => void;
}) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: Loaded }
  >({ status: 'loading' });
  /** Set while a cached day is on screen and the network answer has not arrived.
   *  `state/cache.ts` has documented "cached views paint first and revalidate
   *  behind" since U7 and no screen called it, so the app showed a bare LOADING
   *  on every cold start even with the day already on the device. */
  const [cachedAge, setCachedAge] = useState<string | null>(null);
  /** The schedule collapses past four events. The count was shown but the row
   *  was a plain div — the design labels it `+n more · tap`. */
  const [allEvents, setAllEvents] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewingPhoto, setViewingPhoto] = useState<Photo | null>(null);

  /** Which habit is mid-write, so a double tap cannot log twice. */
  const [loggingHabit, setLoggingHabit] = useState<string | null>(null);

  /** Log a unit, or remove one when the goal is already met.
   *
   *  The same toggle as the Week grid and the TUI's tracker region: tap adds,
   *  and a second tap on a met goal takes one back, which covers a goal of 1
   *  without hiding the count for goals above it. Today showed these chips from
   *  the beginning but never wired them, so the one screen built around closing
   *  out the day was the one screen that could not log a habit.
   */
  async function toggleHabit(habitId: string, date: string, count: number, goal: number) {
    if (loggingHabit) return;
    setLoggingHabit(habitId);
    try {
      const undone = count >= goal && goal > 0;
      if (undone) {
        await undoUnit(session, habitId, date, count);
      } else {
        await logUnit(session, habitId, date);
      }
      // Patch the day in place rather than refetching. A refetch would scroll a
      // long rapid log back to the top for a one-chip change, which is the
      // complaint that got the mutation path reworked in the first place.
      setState((current) =>
        current.status === 'ready' && current.data.tracker
          ? {
              ...current,
              data: {
                ...current.data,
                tracker: {
                  ...current.data.tracker,
                  items: current.data.tracker.items.map((item) =>
                    item.id === habitId
                      ? {
                          ...item,
                          days: item.days.map((d) =>
                            d.journal_date === date
                              ? {
                                  ...d,
                                  // One unit either way. `logs:delete-latest`
                                  // removes the most recent unit, not the day
                                  // — a 6/6 water habit goes to 5/6, not 0/6.
                                  count: undone ? Math.max(0, d.count - 1) : d.count + 1,
                                }
                              : d,
                          ),
                        }
                      : item,
                  ),
                },
              },
            }
          : current,
      );
    } catch {
      // The chip keeps its old count, which is still what the server holds.
    } finally {
      setLoggingHabit(null);
    }
  }

  /** Removable from wherever the photograph can be seen.
   *
   *  This was editor-only at first, on the reasoning that Today is a view of
   *  the day rather than a place to change it. But the reflection *text* on
   *  Today is tap-to-edit, so that reasoning argued the other way, and the
   *  first person to use it went looking for the control here. The two-tap
   *  confirmation is the guard, not the location. */
  async function removePhoto(photo: Photo) {
    try {
      await deletePhoto(session, photo.id);
      setViewingPhoto(null);
      // Local, not a refetch: the day is otherwise unchanged, and refetching
      // would scroll a long rapid log back to the top for a one-row edit.
      setState((current) =>
        current.status === 'ready'
          ? {
              ...current,
              data: {
                ...current.data,
                photos: current.data.photos.filter((p) => p.id !== photo.id),
              },
            }
          : current,
      );
    } catch {
      // The photograph is still on screen and still on the server. Closing the
      // viewer as though it worked would be the worse failure.
    }
  }
  // `date` is optional here — the server decides what today is — so until the
  // first load lands there is no day to compare an event against.
  const shown = date ?? (state.status === 'ready' ? state.data.day.journal_date : null);
  const revalidate = useRevalidate(invalidation ?? null, {
    kind: 'day',
    date: shown ?? '',
  });

  // R12's input. Every event of the day counts, not only the four the schedule
  // shows before `+n more`: the rest are one tap away, and a layout that
  // rearranged itself when you expanded the schedule would be worse than one
  // that decided up front.
  const longestTitle =
    state.status === 'ready'
      ? (state.data.events?.events ?? []).reduce(
          (longest, event) => (event.title.length > longest.length ? event.title : longest),
          '',
        )
      : null;
  useEffect(() => {
    if (!onMeasureTitles || longestTitle === null || !shown) return;
    onMeasureTitles(shown, longestTitle);
  }, [onMeasureTitles, shown, longestTitle]);

  useEffect(() => {
    let cancelled = false;
    const path = date ? `/v1/days/${date}` : '/v1/days/today';

    // Paint whatever is on the device first. This deliberately does not await
    // the network: a cached day on screen in one frame is the whole point.
    void (async () => {
      const destination = activeDestination();
      if (!destination) return;
      const cached = await recall<Loaded>(destination, path);
      if (cancelled || !cached) return;
      setState((current) => (current.status === 'ready' ? current : { status: 'ready', data: cached.value }));
      setCachedAge(ageInWords(cached.storedAt));
    })();

    (async () => {
      try {
        const day = (await request<WireDay>(session, path)).body;
        // Calendar, habits and reflection are each optional subsystems. One
        // being unavailable must not blank the whole day.
        const [events, tracker, reflection, photos] = await Promise.all([
          optional<WireEventRange>(
            session,
            `/v1/calendar/events?from=${day.journal_date}&to=${calendarWindowEnd(day.journal_date)}`,
          ),
          optional<WireTracker>(
            session,
            `/v1/habits/tracker?from=${day.journal_date}&to=${day.journal_date}`,
          ),
          optional<WireReflection>(session, `/v1/days/${day.journal_date}/reflection`),
          // A day with no reflection has no photographs, and 404 is the
          // ordinary case rather than a fault — `optional` already says so.
          optional<{ items: Photo[] }>(
            session,
            `/v1/days/${day.journal_date}/reflection/photos`,
          ),
        ]);
        if (!cancelled) {
          const data = { day, events, tracker, reflection, photos: photos?.items ?? [] };
          setState({ status: 'ready', data });
          setCachedAge(null);
          const destination = activeDestination();
          if (destination) void remember(destination, path, data);
        }
      } catch (error) {
        if (cancelled) return;
        // A cached day already on screen outranks an error banner: the journal
        // is readable, it is just not current, and the banner already says so.
        //
        // This has to be the updater form. Reading `state` here reads the
        // closure captured when the effect ran — `loading` — so the error
        // overwrote the cached paint every time and the banner never appeared.
        setState((current) =>
          current.status === 'ready'
            ? current
            : {
                status: 'error',
                message: error instanceof ApiError ? describeError(error) : String(error),
              },
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, date, revalidate, refresh]);

  if (state.status === 'loading') {
    // Framed as the page will be, so a spread does not jump when it loads.
    return <Frame spread={paired} basis={basis}><SectionHeading muted>LOADING</SectionHeading></Frame>;
  }
  if (state.status === 'error') {
    return (
      <Frame spread={paired} basis={basis}>
        <SectionHeading muted>COULD NOT LOAD</SectionHeading>
        <p style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/var(--bujo-lh-row) var(--bujo-font-mono)', color: 'var(--bujo-text-meta)' }}>
          {state.message}
        </p>
      </Frame>
    );
  }

  const { day, events, tracker, reflection, photos } = state.data;
  // Wrapped rather than point-free: `entryRow` takes an optional second
  // argument and `map` would pass the index into it.
  const rows = day.entries.map((entry) => entryRow(entry));
  const byId = new Map(day.entries.map((e) => [e.id, e]));
  const open = rows.filter((r) => r.state === 'open' || r.state === 'question').length;
  const scheduled = (events?.events ?? []).map(eventRow);
  // A busy calendar must never push the rapid log off screen, so the band caps
  // itself — but the cap is openable rather than final.
  const visible = allEvents ? scheduled : scheduled.slice(0, SCHEDULE_CAP);
  const hidden = scheduled.length - visible.length;
  const habits = (tracker?.items ?? []).map((h) => trackerChip(h, day.journal_date));

  return (
    <Frame spread={lanes === 2 || !includeRitual || paired} basis={basis} active={active}>
      {viewingPhoto ? (
        <PhotoViewer
          session={session}
          photo={viewingPhoto}
          onDismiss={() => setViewingPhoto(null)}
          onRemove={() => void removePhoto(viewingPhoto)}
        />
      ) : null}
      <header
        data-region="header"
        style={{
          paddingBottom: 'var(--bujo-space-3)',
          marginBottom: 'var(--bujo-space-4)',
          borderBottom: '1px solid var(--bujo-rule-heavy)',
        }}
      >
        {chrome === 'page' ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: 'var(--bujo-space-1)' }}>
          <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-eyebrow)', color: 'var(--bujo-text-meta)' }}>
            BUJO / DAILY LOG
          </span>
          {/* `◆ FOCUSED` is a statement about the layout, so it belongs only
              where the one-day focus is a *choice*. Below 720px it is the only
              mode — the spec calls it implicit and unbadged — and on a spread it
              would be a lie printed twice. The spread badges itself instead, on
              the active page only. */}
          {badge ? (
            <span style={{ font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-label)', color: 'var(--bujo-accent)' }}>
              ◆ {badge}
            </span>
          ) : null}
        </div>
        ) : null}
        {cachedAge ? (
          <div style={{ font: 'var(--bujo-fw-bold) var(--bujo-fs-micro)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-badge)', color: 'var(--bujo-accent)', paddingBottom: 'var(--bujo-space-2)' }}>
            LOADING · CACHED VIEW SHOWN FIRST · {cachedAge}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--bujo-space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-label)', color: 'var(--bujo-text-meta)' }}>
            {weekdayLabel(day.journal_date)}
          </span>
          <h1 style={{ margin: 0, font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1 var(--bujo-font-mono)', color: 'var(--bujo-accent)' }}>
            {displayDate(day.journal_date)}
          </h1>
          {showDayOfYear ? (
            <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)', color: 'var(--bujo-text-meta)' }}>
              DAY {dayOfYear(day.journal_date)}
            </span>
          ) : null}
          {chrome === 'spread' && marksToday ? (
            <span style={{ font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-label)', color: 'var(--bujo-accent)' }}>
              ◆ TODAY
            </span>
          ) : null}
        </div>
        {onChangeDate && chrome === 'page' ? (
          <DayPager
            journalDate={day.journal_date}
            atToday={!date}
            onChangeDate={onChangeDate}
            // One pager per spread, on the active page — but the *row* has to
            // exist on both pages. Rendered on the right one only, the right
            // header is taller by exactly this row, and the heavy rule below it
            // plus every section, entry and lane after it sits lower than its
            // twin on the left. The inactive page renders the same markup,
            // inert and invisible, so the two headers match by construction
            // rather than by two paddings being kept in sync by hand.
            inert={!showPaging}
          />
        ) : null}
      </header>

      {/* Two lanes at the wide breakpoint: schedule beside the rapid log, which
          is the terminal spread at a width where it does not clip. Stacked
          below it, because side-by-side in a phone column would be exactly the
          denser-journal the breakpoint rule forbids. */}
      <div
        style={
          lanes === 2
            ? { display: 'flex', gap: 'var(--bujo-space-5)', alignItems: 'flex-start' }
            : undefined
        }
      >
      <section data-region="schedule" style={lanes === 2 ? { flex: '0 0 42%', minWidth: 0 } : { paddingBottom: 'var(--bujo-space-5)' }}>
        {/* Staleness is stated, not merely dimmed. The service sends
            `current | cached | stale | unavailable` plus an `as_of`, and a
            dimmed row alone does not tell you the schedule is hours old. */}
        <SectionHeading muted
          note={
            events
              ? [
                  'READ-ONLY',
                  `${scheduled.length} EVENT${scheduled.length === 1 ? '' : 'S'}`,
                  ...scheduleFreshness(events),
                ].join(NOTE_SEPARATOR)
              : 'NOT CONNECTED'
          }
        >
          SCHEDULE
        </SectionHeading>
        {visible.length === 0 ? (
          <EmptyState line="No events today." />
        ) : (
          visible.map((event, index) => {
            const raw = (events?.events ?? [])[index];
            return (
              <Pressable
                key={event.id}
                label={`${event.time} ${event.title}, ${event.calendar} — read-only detail`}
                onPress={() => {
                  if (raw && onOpenEvent) {
                    onOpenEvent(raw, {
                      asOf: events?.as_of ?? null,
                      freshness: events?.freshness ?? null,
                    });
                  }
                }}
              >
                <EventRow
                  time={event.time}
                  title={event.title}
                  calendar={event.calendar}
                  monogram={event.monogram}
                  accent={event.accent}
                  stale={events?.freshness === 'stale'}
                />
              </Pressable>
            );
          })
        )}
        {hidden > 0 || allEvents ? (
          <button
            type="button"
            onClick={() => setAllEvents(!allEvents)}
            style={{
              minHeight: 'var(--bujo-row-min)',
              textAlign: 'left',
              width: '100%',
              background: 'none',
              border: 0,
              padding: 0,
              font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
              color: 'var(--bujo-accent)',
              cursor: 'pointer',
            }}
          >
            {allEvents ? 'fewer · tap' : `+${hidden} more · tap`}
          </button>
        ) : null}
      </section>

      <section data-region="rapid-log" style={lanes === 2 ? { flex: 1, minWidth: 0 } : { paddingBottom: 'var(--bujo-space-5)' }}>
        <SectionHeading active note={`${open} OPEN · ${rows.length} TOTAL`}>RAPID LOG</SectionHeading>
        {rows.length === 0 ? (
          <EmptyState line="Nothing logged yet." />
        ) : (
          rows.map((row) => {
            const entry = byId.get(row.id);
            // Only the two transitions a toast can undo, and only where the
            // server would accept them: complete needs an open task, migrate
            // needs `Placement::Date`. Offering a swipe the service refuses is
            // worse than offering none.
            const canComplete = entry?.kind === 'task' && entry.state === 'open';
            const canMigrate =
              entry?.kind === 'task' &&
              entry.state === 'open' &&
              Boolean(entry.active_date) &&
              !entry.active_month;
            return (
              <SwipeRow
                key={row.id}
                onLeft={canComplete && entry && onQuickAction ? () => onQuickAction(entry, 'complete') : undefined}
                onRight={canMigrate && entry && onQuickAction ? () => onQuickAction(entry, 'migrate') : undefined}
              >
                <EntryLine
                  entry={row}
                  selected={row.id === selectedId}
                  onPress={() => {
                    if (row.id === selectedId) {
                      if (entry) onOpenActions(entry);
                    } else {
                      setSelectedId(row.id);
                    }
                  }}
                />
              </SwipeRow>
            );
          })
        )}
      </section>

      </div>

      {includeRitual ? (
      <div style={{ borderTop: '1px solid var(--bujo-rule-heavy)', paddingTop: 'var(--bujo-space-4)' }}>
        <section style={{ paddingBottom: 'var(--bujo-space-4)' }}>
          <SectionHeading muted note={reflection?.content ? 'TAP TO EDIT' : 'TAP TO WRITE'}>
            REFLECTION
          </SectionHeading>
          <button
            type="button"
            onClick={onEditReflection}
            aria-label={reflection?.content ? 'Edit today\u2019s reflection' : 'Write today\u2019s reflection'}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              minHeight: 'var(--bujo-row-min)',
              cursor: 'pointer',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            {reflection?.content ? (
              <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-prose) var(--bujo-font-sans)', color: 'var(--bujo-text-primary)' }}>
                {reflection.content}
              </span>
            ) : (
              <EmptyState line="Not written." />
            )}
          </button>
          {/* Outside the button: a photograph opens the photograph, not the
              editor, and a button inside a button is not valid markup. */}
          <PhotoStrip session={session} photos={photos} onOpen={setViewingPhoto} />
        </section>

        <section>
          <SectionHeading muted
            note={`${habits.length} HABIT${habits.length === 1 ? '' : 'S'} · ${habits.filter((h) => h.count > 0).length} LOGGED`}
          >
            TRACKERS
          </SectionHeading>
          {habits.length === 0 ? (
            <EmptyState line="No habits yet." />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bujo-space-2)', paddingTop: 'var(--bujo-space-2)' }}>
              {habits.map((habit) => (
                <TrackerToggle
                  key={habit.id}
                  name={habit.name}
                  done={habit.count}
                  goal={habit.goal}
                  busy={loggingHabit === habit.id}
                  onToggle={() =>
                    void toggleHabit(habit.id, day.journal_date, habit.count, habit.goal)
                  }
                />
              ))}
            </div>
          )}
        </section>
      </div>
      ) : null}
    </Frame>
  );
}

/** `STALE · AS OF 09:12 · 2H OLD`, or nothing when the schedule is current.
 *
 *  `unavailable` is its own case: there is no `as_of` to report because nothing
 *  was read, and saying "as of" about a read that never happened would be
 *  worse than saying nothing. */
function scheduleFreshness(events: WireEventRange): string[] {
  const freshness = (events.freshness ?? '').toLowerCase();
  if (!freshness || freshness === 'current' || freshness === 'fresh') return [];
  if (freshness === 'unavailable') return ['UNAVAILABLE'];
  const parts = [freshness.toUpperCase()];
  if (events.as_of) {
    const at = new Date(events.as_of);
    if (!Number.isNaN(at.getTime())) {
      parts.push(
        `AS OF ${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`,
      );
      parts.push(ageInWords(events.as_of));
    }
  }
  return parts;
}

/** The day before this one. `nextDay` lives in mutations because migration
 *  needs it; this is its mirror and belongs beside its only caller. */
const dayNav = {
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  cursor: 'pointer',
};

/** Move the spread. Belongs to the day you are on, but occupies its row on
 *  every page so a spread's two headers are the same height.
 *
 *  The inactive page renders the **same elements**, hidden. An earlier version
 *  substituted spans for the buttons and came out 16px shorter: under
 *  `align-items: baseline` a button's baseline sits differently from a span's,
 *  so the row resolved to a different height and the misalignment this exists to
 *  prevent survived at a smaller size. Identical markup is the only version that
 *  matches by construction rather than by arithmetic that has to be maintained.
 *
 *  `visibility: hidden` takes the buttons out of the tab order and the
 *  accessibility tree; `aria-hidden`, `tabIndex` and dropped handlers mean a
 *  duplicate pager cannot be reached or fired even if that ever changed.
 *
 *  The row centres rather than baseline-aligns, and that is load-bearing. The
 *  middle item is a `<span>` on the day you are already on and a `<button>`
 *  otherwise, and under `align-items: baseline` those resolve to different row
 *  heights — 64px against 48px — because a button's baseline sits centred in its
 *  44px tap box while a span's sits in its text line. Since every label here is
 *  one line at one size, centring reads identically and makes the height depend
 *  on the tap minimum alone.
 */
function DayPager({
  journalDate,
  atToday,
  onChangeDate,
  inert,
}: {
  journalDate: string;
  atToday: boolean;
  onChangeDate: (date: string | null) => void;
  inert: boolean;
}) {
  const go = (date: string | null) => (inert ? undefined : () => onChangeDate(date));
  const step = inert ? { ...dayNav, cursor: 'default' } : dayNav;

  return (
    <nav
      aria-label={inert ? undefined : 'Change day'}
      aria-hidden={inert ? 'true' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 'var(--bujo-space-1)',
        ...(inert ? { visibility: 'hidden', pointerEvents: 'none' } : null),
      }}
    >
      <button type="button" tabIndex={inert ? -1 : undefined} onClick={go(previousDay(journalDate))} style={step}>
        ◀ {displayDate(previousDay(journalDate)).slice(0, 6)}
      </button>
      {atToday ? (
        <span style={{ ...dayNav, cursor: 'default', display: 'inline-flex', alignItems: 'center' }}>
          ◆ TODAY
        </span>
      ) : (
        <button type="button" tabIndex={inert ? -1 : undefined} onClick={go(null)} style={step}>
          today
        </button>
      )}
      <button type="button" tabIndex={inert ? -1 : undefined} onClick={go(nextDay(journalDate))} style={step}>
        {displayDate(nextDay(journalDate)).slice(0, 6)} ▶
      </button>
    </nav>
  );
}

function Frame({
  children,
  spread,
  basis,
  active,
}: {
  children: unknown;
  basis?: string;
  /** On a spread the page fills its column instead of centring in 720px, and
   *  scrolls independently of its neighbour. */
  spread?: boolean;
  active?: boolean;
}) {
  return (
    <main
      data-day-page=""
      style={
        spread
          ? {
              padding: 'var(--bujo-gutter)',
              flex: basis ? `0 0 ${basis}` : 1,
              minWidth: 0,
              // Columns scroll independently above the first breakpoint.
              overflowY: 'auto',
              // The active page carries the amber edge, and the inactive one a
              // hairline — never colour alone, so the edge has a weight
              // difference too.
              borderLeft: active
                ? 'var(--bujo-select-edge-width) solid var(--bujo-select-edge)'
                : '1px solid var(--bujo-rule-hairline)',
            }
          : { padding: 'var(--bujo-gutter)', maxWidth: 720, margin: '0 auto' }
      }
    >
      {children as never}
    </main>
  );
}

/** Optional subsystems answer 404 or 503 when not configured. That is a state,
 *  not an error: the day still renders without them. */
async function optional<T>(session: Session, path: string): Promise<T | null> {
  try {
    return (await request<T>(session, path)).body;
  } catch (error) {
    if (error instanceof ApiError && [404, 409, 503].includes(error.status)) return null;
    if (error instanceof ApiError && error.status === 422) return null;
    throw error;
  }
}
